import AES from 'crypto-js/aes'
import EncUtf8 from 'crypto-js/enc-utf8'

import { defaultUserlikeGroup, oldDataTimeoutInHours } from 'consts'
import isSessionStorageExists from 'utils/isSessionStorageExists'
import { CommunicationService } from 'entities/CommunicationService'
import { AvailableCampaigns, Languages } from 'entities/Config'
import type Message from 'entities/Message'
import type ContextOfConversation from 'entities/ContextOfConversation'
import type { ChatBackend } from 'entities/CommunicationService'

export enum SessionStorageKey {
  SessionStorageMessagesKey = 'sae-conversational-messages',
  SessionStorageMessagesBufferKey = 'sae-conversational-msg-buffer',
  SessionStorageContextsKey = 'sae-conversational-contexts',
  SessionStorageLastUserKey = 'sae-conversational-last-user',
  SessionStorageUserlike = 'sae-conversational-userlike',
  SessionStorageUserlikeGroup = 'sae-conversational-userlike-group',
  SessionStorageSettings = 'sae-conversational-settings',
  SessionStorageLanguage = 'sae-conversational-language',
  CrossSellWidgetData = 'crosssellwidgetdata',
}

class CustomStorage {
  private storage: Partial<Record<SessionStorageKey, string>> = {}

  public getItem = (key: SessionStorageKey): string | null =>
    this.storage[key] || null

  public setItem = (key: SessionStorageKey, value: string): void => {
    this.storage[key] = value
  }

  public removeItem = (key: SessionStorageKey): void => {
    delete this.storage[key]
  }

  public clear = (): void => {
    this.storage = {}
  }
}

export const storage = isSessionStorageExists()
  ? sessionStorage
  : new CustomStorage()

export const clearSessionStorage = () => {
  storage.removeItem(SessionStorageKey.SessionStorageMessagesKey)
  storage.removeItem(SessionStorageKey.SessionStorageMessagesBufferKey)
  storage.removeItem(SessionStorageKey.SessionStorageContextsKey)
  storage.removeItem(SessionStorageKey.SessionStorageLastUserKey)
  storage.removeItem(SessionStorageKey.SessionStorageUserlike)
  storage.removeItem(SessionStorageKey.SessionStorageUserlikeGroup)
}

const loadItem = (
  key: SessionStorageKey,
  defaultValue: any,
  decrypt?: boolean
): any => {
  try {
    const serialisedValue = storage.getItem(key)
    if (serialisedValue === null || typeof serialisedValue === 'undefined') {
      return defaultValue
    }

    if (decrypt && process.env.REACT_APP_KITTEN_TUPEL) {
      const bytes = AES.decrypt(
        serialisedValue,
        process.env.REACT_APP_KITTEN_TUPEL
      )

      return JSON.parse(bytes.toString(EncUtf8))
    }

    return JSON.parse(serialisedValue)
  } catch (error) {
    if (error instanceof SyntaxError) {
      // eslint-disable-next-line no-console
      console.error(
        `Local storage contains invalid JSON under ${key}. Clearing whole sessionStorage`
      )
      clearSessionStorage()
    } else {
      // eslint-disable-next-line no-console
      console.error(
        `Failed to load ${key} from local storage: ${(error as any).message}`
      )
    }
    return defaultValue
  }
}

export const saveItem = (
  key: SessionStorageKey,
  value: any,
  encrypt?: boolean
): void => {
  try {
    let serialisedValue = JSON.stringify(value)

    if (encrypt && process.env.REACT_APP_KITTEN_TUPEL) {
      serialisedValue = AES.encrypt(
        serialisedValue,
        process.env.REACT_APP_KITTEN_TUPEL
      ).toString()
    }

    storage.setItem(key, serialisedValue)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to save ${key} to local storage: ${(error as any).message}`
    )
  }
}

export const loadMessagesFromSessionStorage = (
  key = SessionStorageKey.SessionStorageMessagesKey
): Array<Message> => {
  const messages: Array<Message> = loadItem(key, [], true)
  if (!Array.isArray(messages) || messages.length === 0) {
    return []
  }

  const nowDate = new Date().getTime()
  const lastMessageDate = new Date(
    messages[messages.length - 1].timestamp
  ).getTime()

  const hoursInMilliseconds = 1000 * 60 * 60 * oldDataTimeoutInHours

  if (nowDate - lastMessageDate > hoursInMilliseconds) {
    clearSessionStorage()
    return []
  }

  return messages.map((item: Message) => ({
    ...item,
    timestamp: new Date(item.timestamp),
  }))
}

export const saveMessagesToSessionStorage = (
  responses: Array<Message>,
  key: SessionStorageKey = SessionStorageKey.SessionStorageMessagesKey
): void => {
  const filtredResponses = responses.filter((el) => !el.messageObject.protected)
  saveItem(
    key,
    filtredResponses.map((el) => ({
      ...el,
      messageObject: {
        ...el.messageObject,
        delayBeforeAppear: undefined,
        delayBeforeAppend: undefined,
      },
    })),
    true
  )
}

export const saveBufferMessagesToSessionStorage = (
  responses: Array<Message>
): void => {
  saveMessagesToSessionStorage(
    responses,
    SessionStorageKey.SessionStorageMessagesBufferKey
  )
}

export const removeLastBufferMessageItemFromSessionStorage = () => {
  const bufferMessages = loadItem(
    SessionStorageKey.SessionStorageMessagesBufferKey,
    [],
    true
  )

  const updatedMessages = bufferMessages.splice(1)

  saveMessagesToSessionStorage(
    updatedMessages,
    SessionStorageKey.SessionStorageMessagesBufferKey
  )
}

export const clearMessageBufferFromSessionStorage = () => {
  storage.removeItem(SessionStorageKey.SessionStorageMessagesBufferKey)
}

export const mergeBufferMessagesInSessionStorage = () => {
  const bufferMessages = loadItem(
    SessionStorageKey.SessionStorageMessagesBufferKey,
    [],
    true
  )

  if (bufferMessages.length) {
    const messages = loadItem(
      SessionStorageKey.SessionStorageMessagesKey,
      [],
      true
    )
    const mergedMessages = [...messages, ...bufferMessages]

    saveMessagesToSessionStorage(mergedMessages)
  }

  clearMessageBufferFromSessionStorage()
}

export const loadMessageBufferFromSessionStorage = () =>
  loadMessagesFromSessionStorage(
    SessionStorageKey.SessionStorageMessagesBufferKey
  )

export const loadContextsFromSessionStorage =
  (): Array<ContextOfConversation> =>
    loadItem(SessionStorageKey.SessionStorageContextsKey, [], true)

export const saveContextsToSessionStorage = (
  contexts: Array<ContextOfConversation>
): void => saveItem(SessionStorageKey.SessionStorageContextsKey, contexts, true)

export const saveStateToSessionStorage = (
  messages: Array<Message>,
  contexts: Array<ContextOfConversation>
) => {
  saveContextsToSessionStorage(contexts)
  saveMessagesToSessionStorage(messages)
}

export const loadStateFromSessionStorage = () => ({
  contexts: loadContextsFromSessionStorage(),
  messages: loadMessagesFromSessionStorage(),
})

export const setUserlikeSession = (value: boolean) => {
  if (value) {
    saveItem(SessionStorageKey.SessionStorageUserlike, 'true')
  } else {
    storage.removeItem(SessionStorageKey.SessionStorageUserlike)
  }
}

export const setUserlikeGroup = (group: string) => {
  saveItem(SessionStorageKey.SessionStorageUserlikeGroup, group)
}

export const getUserlikeGroup = () =>
  loadItem(SessionStorageKey.SessionStorageUserlikeGroup, defaultUserlikeGroup)

export const removeUserlikeGroup = () => {
  storage.removeItem(SessionStorageKey.SessionStorageUserlikeGroup)
}

export const getUserlikeSession = () =>
  loadItem(SessionStorageKey.SessionStorageUserlike, null)

export const encryptValue = (key: SessionStorageKey) => {
  let serialisedValue = storage.getItem(key)

  if (serialisedValue === null || typeof serialisedValue === 'undefined') {
    return
  }

  try {
    JSON.parse(serialisedValue)

    if (process.env.REACT_APP_KITTEN_TUPEL) {
      serialisedValue = AES.encrypt(
        serialisedValue,
        process.env.REACT_APP_KITTEN_TUPEL
      ).toString()

      storage.setItem(key, serialisedValue)
    }
  } catch (_) {} // eslint-disable-line
}

export const encryptCurrentValues = () => {
  encryptValue(SessionStorageKey.SessionStorageMessagesKey)
  encryptValue(SessionStorageKey.SessionStorageContextsKey)
}

export const getBackendSession = () => {
  const { backend } = loadItem(SessionStorageKey.SessionStorageSettings, {
    backend: CommunicationService.A,
  })

  return backend
}

export const setBackendSession = (backend: ChatBackend) => {
  const settings = loadItem(SessionStorageKey.SessionStorageSettings, {})
  saveItem(SessionStorageKey.SessionStorageSettings, {
    ...settings,
    backend,
  })
}

export const isBackend = (backends: Array<ChatBackend>) =>
  backends.includes(getBackendSession())

export const loadSettingsItems = () =>
  loadItem(SessionStorageKey.SessionStorageSettings, {})

export const saveSettingsItems = (properties: object) => {
  const settings = loadItem(SessionStorageKey.SessionStorageSettings, {})
  saveItem(SessionStorageKey.SessionStorageSettings, {
    ...settings,
    ...properties,
  })
}

export const clearSettingsItems = (key: string) => {
  const settings = loadItem(SessionStorageKey.SessionStorageSettings, {})
  delete settings[key]
  saveItem(SessionStorageKey.SessionStorageSettings, settings)
}

export const setLanguageInSessionStorage = (language: string) => {
  saveItem(SessionStorageKey.SessionStorageLanguage, language)
}

export const getLanguageInSessionStorage = () =>
  loadItem(SessionStorageKey.SessionStorageLanguage, Languages.DE)

export const getConsultId = (themeId: string) => {
  const settingsItems = loadSettingsItems()
  return settingsItems?.[`campaign-${themeId}`]?.consultID
}

export const setLastScrollPoint = (top: number, themeId: string) => {
  const settingsItems = loadSettingsItems()
  if (themeId === AvailableCampaigns.default) {
    settingsItems.lastScrollPoint = top
  } else if (`campaign-${themeId}` in settingsItems) {
    settingsItems[`campaign-${themeId}`].lastScrollPoint = top
  }

  saveItem(SessionStorageKey.SessionStorageSettings, settingsItems)
}

export const getLastScrollPoint = (themeId: string) => {
  const settingsItems = loadSettingsItems()
  if (settingsItems?.[`campaign-${themeId}`]) {
    return settingsItems?.[`campaign-${themeId}`]?.lastScrollPoint
  }

  return settingsItems?.lastScrollPoint
}

export const clearScrollPoint = (themeId: string) => {
  const settingsItems = loadSettingsItems()
  if (settingsItems?.[`campaign-${themeId}`]) {
    delete settingsItems?.[`campaign-${themeId}`]?.lastScrollPoint
  } else {
    delete settingsItems?.lastScrollPoint
  }

  saveItem(SessionStorageKey.SessionStorageSettings, settingsItems)
}

export const getActiveCampaignFromStorage = () => {
  const settingsItems = loadSettingsItems()
  const activeCampaign = settingsItems?.activeCampaign

  const campaign = activeCampaign && settingsItems?.[activeCampaign]

  if (campaign) {
    campaign.id = activeCampaign.slice('campaign-'.length)
  }

  return campaign
}
